import { Link } from "react-router-dom";
import { Navbar } from "../../components/Navbar";
import { Footer } from "../../components/Footer";

/** Support: how to reach us and fixes for common problems. Also the Support URL on the Zoom Marketplace listing. */
export default function Support() {
  return (
    <div className="min-h-screen bg-white font-body text-slate-800">
      <Navbar />
      <main>
        <article className="mx-auto max-w-3xl px-6 py-12 leading-relaxed">
          <header className="mb-10 border-b border-slate-200 pb-6">
            <h1 className="text-4xl font-semibold text-slate-900">Support</h1>
            <p className="mt-2 text-sm text-slate-500">Last updated: 2026-10-17</p>
          </header>

          <Section n="1" title="Contact us">
            <p>
              Email{" "}
              <a href="mailto:hello@intakall.io" className="text-indigo-600 hover:underline">hello@intakall.io</a>
              {" "}and a person on our team will usually reply within one business day (UK time, Monday to Friday).
            </p>
            <p>To help us sort it out quickly, tell us:</p>
            <ul className="ml-6 list-disc space-y-1">
              <li>the email address you sign in to Intakall with;</li>
              <li>where you use Intakall (Zoom or the Chrome extension);</li>
              <li>roughly when it happened, and what you expected to see.</li>
            </ul>
            <p>
              Privacy or data requests:{" "}
              <a href="mailto:privacy@intakall.io" className="text-indigo-600 hover:underline">privacy@intakall.io</a>.
              Security reports: see our{" "}
              <Link to="/security" className="text-indigo-600 hover:underline">Security Trust Centre</Link>.
            </p>
          </Section>

          <Section n="2" title="Guides">
            <ul className="ml-6 list-disc space-y-1">
              <li>
                <Link to="/zoom" className="text-indigo-600 hover:underline">Intakall for Zoom</Link>: adding, using
                and removing the Zoom app.
              </li>
            </ul>
          </Section>

          <Section n="3" title="Common questions">
            <h3 className="text-lg font-medium text-slate-900">My teammates don't see any cards</h3>
            <p>
              Cards are shared with teammates only when the recording is set to <strong>Team</strong>, and only with
              people who are in the same Intakall team. Check that everyone has joined your team in Intakall.
            </p>
            <h3 className="text-lg font-medium text-slate-900">No cards appear at all</h3>
            <p>
              Cards come from the work tools your team has connected. Open the Integrations page at{" "}
              <a href="https://app.intakall.io" className="text-indigo-600 hover:underline">app.intakall.io</a>
              {" "}and check at least one tool is connected.
            </p>
            <h3 className="text-lg font-medium text-slate-900">I can't find Intakall in my Zoom meeting</h3>
            <p>
              Click <strong>Apps</strong> in the meeting toolbar. If Intakall isn't listed, add it from the Zoom App
              Marketplace first, or ask your Zoom admin to allow it.
            </p>
            <h3 className="text-lg font-medium text-slate-900">How do I delete my data?</h3>
            <p>
              Delete meetings in Intakall, or email{" "}
              <a href="mailto:privacy@intakall.io" className="text-indigo-600 hover:underline">privacy@intakall.io</a>
              {" "}to delete your account. See the{" "}
              <Link to="/privacy" className="text-indigo-600 hover:underline">Privacy Policy</Link>.
            </p>
          </Section>
        </article>
      </main>
      <Footer />
    </div>
  );
}

function Section({ n, title, children }: { n: string; title: string; children: React.ReactNode }) {
  return (
    <section className="mt-10">
      <h2 className="text-2xl font-semibold text-slate-900">
        <span className="mr-3 text-slate-400">{n}.</span>
        {title}
      </h2>
      <div className="mt-3 space-y-3">{children}</div>
    </section>
  );
}
